import { api } from '../api';
import {
  PRICE_ALERT_CREATE,
  PRICE_ALERT_LIST,
  priceAlertUpdateUrl
} from '../../constants/priceAlert';
import {
  CreatePriceAlertRequest,
  GetPriceAlertListRequest,
  GetPriceAlertListResponse,
  PriceAlertResponse,
  PriceAlertStatus,
  UpdatePriceAlertRequest
} from './types';

export const priceAlertSlice = api.injectEndpoints({
  endpoints: (build) => ({
    // ========================================================================
    // CREATE — POST market/price-alert
    // ========================================================================
    createPriceAlert: build.mutation<
      PriceAlertResponse,
      CreatePriceAlertRequest
    >({
      query: (body) => ({
        url: PRICE_ALERT_CREATE,
        method: 'POST',
        body
      }),
      invalidatesTags: ['PriceAlerts']
    }),

    // ========================================================================
    // LIST — POST market/price-alert/list
    //
    // Pagination is a 0-based item offset, not a page index. Passing `id`
    // returns that single alert; there is no GET by id.
    // ========================================================================
    getPriceAlertList: build.query<
      GetPriceAlertListResponse,
      GetPriceAlertListRequest
    >({
      query: (body) => ({
        url: PRICE_ALERT_LIST,
        method: 'POST',
        body
      }),
      providesTags: ['PriceAlerts']
    }),

    // ========================================================================
    // EDIT — PATCH market/price-alert/{id}
    //
    // Channels are add-only; they can never be removed. Cancelling cannot be
    // combined with target_price or add_channels.
    // ========================================================================
    updatePriceAlert: build.mutation<
      PriceAlertResponse,
      UpdatePriceAlertRequest
    >({
      query: ({ id, ...body }) => ({
        url: priceAlertUpdateUrl(id),
        method: 'PATCH',
        body
      }),
      invalidatesTags: ['PriceAlerts']
    }),

    // ========================================================================
    // CANCEL — the API has no delete. The alert stays in the list as history.
    //
    // Idempotent: cancelling an already-cancelled alert succeeds. Cancelling a
    // TRIGGERED alert fails, because TRIGGERED is terminal. Cancelling never
    // refunds quota.
    // ========================================================================
    cancelPriceAlert: build.mutation<PriceAlertResponse, { id: string }>({
      query: ({ id }) => ({
        url: priceAlertUpdateUrl(id),
        method: 'PATCH',
        body: { status: PriceAlertStatus.CANCELED }
      }),
      invalidatesTags: ['PriceAlerts']
    })
  }),
  overrideExisting: false
});

export const {
  useCreatePriceAlertMutation,
  useGetPriceAlertListQuery,
  useUpdatePriceAlertMutation,
  useCancelPriceAlertMutation
} = priceAlertSlice;
